import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Box,
  IconButton,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import axios from 'axios';

/**
 * Модальное окно создания/редактирования автора произведения.
 * @param {boolean} open - открыт ли диалог
 * @param {function} onClose - колбэк при закрытии
 * @param {object|null} author - автор для редактирования (null = создание)
 * @param {function} onSuccess - колбэк при успешном сохранении (author) => void
 */
/** Парсит full_name в фамилию, имя, отчество (по пробелам). */
function parseFullName(fullName) {
  if (!fullName || !String(fullName).trim()) {
    return { last_name: '', first_name: '', patronymic: '' };
  }
  const parts = String(fullName).trim().split(/\s+/);
  return {
    last_name: parts[0] || '',
    first_name: parts[1] || '',
    patronymic: parts[2] || '',
  };
}

export default function AuthorFormModal({ open, onClose, author, onSuccess }) {
  const isEdit = !!author;

  const [formData, setFormData] = useState({
    last_name: '',
    first_name: '',
    patronymic: '',
    birth_year: '',
    death_year: '',
    bio: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);

  useEffect(() => {
    if (open) {
      setError(null);
      setPhotoFile(null);
      setPhotoPreviewUrl(null);
      setPhotoRemoved(false);
      if (author) {
        const { last_name, first_name, patronymic } = parseFullName(author.full_name);
        setFormData({
          last_name,
          first_name,
          patronymic,
          birth_year: author.birth_year ?? '',
          death_year: author.death_year ?? '',
          bio: author.bio || '',
        });
      } else {
        setFormData({
          last_name: '',
          first_name: '',
          patronymic: '',
          birth_year: '',
          death_year: '',
          bio: '',
        });
      }
    }
  }, [open, author]);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|gif|webp|bmp)$/i.test(file.type)) {
      setError('Выберите изображение: JPG, PNG, GIF, WEBP или BMP');
      return;
    }
    setError(null);
    setPhotoFile(file);
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemovePhoto = async () => {
    setPhotoFile(null);
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
      setPhotoPreviewUrl(null);
    }
    if (author?.photo_path) {
      try {
        await axios.delete(`/api/authors/${author.id}/photo`);
        setPhotoRemoved(true);
      } catch (err) {
        setError(err.response?.data?.error || 'Не удалось удалить фото');
      }
    }
  };

  const currentPhotoUrl = photoPreviewUrl ?? (!photoRemoved && author?.photo_path ? `/uploads/${author.photo_path}` : null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const full_name = [formData.last_name, formData.first_name, formData.patronymic]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' ');

    try {
      const payload = {
        full_name,
        birth_year: formData.birth_year ? parseInt(formData.birth_year, 10) : null,
        death_year: formData.death_year ? parseInt(formData.death_year, 10) : null,
        bio: formData.bio.trim() || null,
      };

      let savedAuthor;
      if (isEdit) {
        const response = await axios.put(`/api/authors/${author.id}`, payload);
        savedAuthor = response.data;
      } else {
        const response = await axios.post('/api/authors', payload);
        savedAuthor = response.data;
      }
      if (photoFile && savedAuthor?.id) {
        const formData = new FormData();
        formData.append('file', photoFile);
        const photoResponse = await axios.post(
          `/api/authors/${savedAuthor.id}/photo`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        savedAuthor = photoResponse.data;
      }
      onSuccess?.(savedAuthor);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения автора');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? 'Редактирование автора' : 'Новый автор'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            required
            label="Фамилия"
            value={formData.last_name}
            onChange={(e) =>
              setFormData({ ...formData, last_name: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Имя"
            value={formData.first_name}
            onChange={(e) =>
              setFormData({ ...formData, first_name: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Отчество"
            value={formData.patronymic}
            onChange={(e) =>
              setFormData({ ...formData, patronymic: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="number"
            label="Год рождения"
            value={formData.birth_year}
            onChange={(e) =>
              setFormData({ ...formData, birth_year: e.target.value })
            }
            inputProps={{ min: 1000, max: 2100 }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="number"
            label="Год смерти"
            value={formData.death_year}
            onChange={(e) =>
              setFormData({ ...formData, death_year: e.target.value })
            }
            inputProps={{ min: 1000, max: 2100 }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<PhotoCamera />}
              >
                {(author?.photo_path && !photoRemoved) || photoFile ? 'Заменить фото' : 'Добавить фото автора'}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
                  onChange={handlePhotoChange}
                />
              </Button>
              {((author?.photo_path && !photoRemoved) || photoFile) && (
                <IconButton size="small" onClick={handleRemovePhoto} title="Удалить фото" color="error">
                  <DeleteOutline />
                </IconButton>
              )}
            </Box>
            {currentPhotoUrl && (
              <Box
                component="img"
                src={currentPhotoUrl}
                alt="Фото автора"
                sx={{
                  mt: 1,
                  maxWidth: 120,
                  maxHeight: 120,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            )}
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Биография"
            value={formData.bio}
            onChange={(e) =>
              setFormData({ ...formData, bio: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.last_name.trim()}
          >
            {loading ? <CircularProgress size={24} /> : isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
